// @flow
/**
 * Version choices for a custom widget instance.
 *
 * An empty version means "latest published": the server resolves it to the
 * widget's published version in the editor (customWidgetForRender) and at
 * deploy (resolveCustomWidgetData), so the page updates whenever a new version
 * is published and the page is deployed. A number pins that version. "draft"
 * shows the unpublished draft in the editor only; deploys and published renders
 * use the published version instead.
 */

export const LATEST = '';
export const DRAFT = 'draft';

type Version = { versionId: string };

const parse = (v: string): ?Array<number> => {
  if (!/^\d+(\.\d+)*$/.test(v || '')) return null;
  return v.split('.').map(Number);
};

/** Negative when a < b, positive when a > b, 0 when equal or not comparable. */
export const compareVersions = (a: string, b: string): number => {
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return 0;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
};

/**
 * Options for the version select, newest numbered version first. The current
 * selection is always included, so a select never silently shows "latest"
 * for a pin whose version isn't listed (not loaded yet, or removed).
 */
export const getVersionOptions = (versions: Array<Version>, publishedVersion: ?string, selected: ?string) => {
  const numbered = (versions || [])
    .filter(v => v && v.versionId && v.versionId !== DRAFT)
    .map(v => v.versionId)
    .sort((a, b) => compareVersions(b, a));
  const options = [{
    value: LATEST,
    label: `Latest published${publishedVersion ? ` (v${publishedVersion})` : ''} · updates automatically`
  }];
  numbered.forEach((id) => {
    options.push({ value: id, label: `Pin to v${id}${id === publishedVersion ? ' (published)' : ''}` });
  });
  if ((versions || []).some(v => v && v.versionId === DRAFT)) {
    options.push({ value: DRAFT, label: 'Draft · editor preview only' });
  }
  if (selected && !options.some(o => o.value === selected)) {
    options.push(selected === DRAFT
      ? { value: DRAFT, label: 'Draft · editor preview only' }
      : { value: selected, label: `Pin to v${selected}` });
  }
  return options;
};

/**
 * A short status for the selected version, or null when nothing needs saying.
 * tone: 'info' | 'warn'
 */
export const getVersionStatus = (selected: ?string, publishedVersion: ?string) => {
  const value = selected || LATEST;
  if (value === LATEST) return { tone: 'info', text: 'updates automatically' };
  if (value === DRAFT) return { tone: 'warn', text: 'live page shows the published version' };
  if (publishedVersion && compareVersions(value, publishedVersion) < 0) return { tone: 'warn', text: 'pinned to an older version' };
  return { tone: 'info', text: 'pinned' };
};
